import { useQuery } from '@tanstack/react-query';

import { showErrorToast } from '@hooks/showErrorToast';
import { request } from '@utils';

export interface Template {
  id: string;
  name: string;
  description: string;
}

export interface TestStep {
  name: string;
  order: number;
  request: {
    method: string;
    endpoint: string;
    headers?: Record<string, string>;
    body?: any;
    params?: any;
    query?: any;
  };
  response: {
    status: number;
    body?: any;
  };
  databaseChanges: {
    collection: string;
    operation: string;
    documentId?: string;
    changes?: Record<string, any>;
  }[];
  timestamp: Date;
  duration?: number;
  requestId: string;
}

export interface TestPhase {
  phase: {
    name: string;
    order: number;
  };
  steps: TestStep[];
}

export interface TestGeneratorResponse {
  batch: {
    _id: string;
    name: string;
    counter: number;
  };
  template: Template;
  phases: TestPhase[];
}

export interface TestDefinition {
  metadata: {
    template: string;
    batchId: string;
    batchName?: string;
    batchCounter?: number;
    generatedAt: string;
  };
  steps: TestStep[];
}

// Get available templates
export const useTestTemplates = () => {
  return useQuery({
    queryKey: ['test-templates'],
    queryFn: async (): Promise<{ templates: Template[] }> => {
      try {
        return await request({
          path: 'admin/test-generator/templates',
          method: 'GET',
          authenticate: true,
        });
      } catch (error) {
        showErrorToast(error, 'Failed to fetch test templates');
        return { templates: [] };
      }
    },
  });
};

// Get test data for a batch
export const useTestGenerator = (batchId: string, templateId: string, enabled = false) => {
  return useQuery({
    queryKey: ['test-generator', batchId, templateId],
    queryFn: async (): Promise<TestGeneratorResponse | null> => {
      try {
        const queryParams = new URLSearchParams({
          template: templateId,
        });

        return await request({
          path: `admin/test-generator/batch/${batchId}?${queryParams.toString()}`,
          method: 'GET',
          authenticate: true,
        });
      } catch (error) {
        showErrorToast(error, 'Failed to generate test data');
        return null;
      }
    },
    enabled: enabled && !!batchId && !!templateId,
  });
};

// Export test definition
export const useExportTestDefinition = () => {
  return async (batchId: string, templateId: string): Promise<TestDefinition | null> => {
    try {
      const queryParams = new URLSearchParams({
        template: templateId,
      });

      return await request({
        path: `admin/test-generator/batch/${batchId}/export?${queryParams.toString()}`,
        method: 'GET',
        authenticate: true,
      });
    } catch (error) {
      showErrorToast(error, 'Failed to export test definition');
      return null;
    }
  };
};
